import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";

export default function CallbackPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<"loading" | "error">("loading");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Supabase automatically handles the OAuth code exchange when detectSessionInUrl is true
        // We just need to wait for the session to be established
        const { data, error } = await supabase.auth.getSession();

        if (error) {
          setErrorMsg(error.message);
          setStatus("error");
          return;
        }

        if (data.session) {
          // Session established — redirect to dashboard
          navigate("/dashboard", { replace: true });
          return;
        }

        // If no session yet, listen for auth state change
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
          if (event === "SIGNED_IN" && session) {
            subscription.unsubscribe();
            navigate("/dashboard", { replace: true });
          } else if (event === "SIGNED_OUT") {
            subscription.unsubscribe();
            setErrorMsg("Sign in was cancelled or failed.");
            setStatus("error");
          }
        });

        // Timeout fallback
        setTimeout(() => {
          subscription.unsubscribe();
          setErrorMsg("Sign in timed out. Please try again.");
          setStatus("error");
        }, 10000);
      } catch (err) {
        setErrorMsg(err instanceof Error ? err.message : "Unexpected error during sign in.");
        setStatus("error");
      }
    };

    handleCallback();
  }, [navigate]);

  if (status === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 px-4">
        <div className="w-full max-w-sm text-center">
          <div className="bg-white rounded-2xl border border-gray-100 p-10">
            <div className="w-14 h-14 flex items-center justify-center rounded-full bg-rose-100 mx-auto mb-5">
              <i className="ri-error-warning-line text-2xl text-rose-500"></i>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Sign in failed</h2>
            <p className="text-sm text-gray-500 mb-6">{errorMsg}</p>
            <a
              href="/login"
              className="inline-block px-6 py-3 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white font-semibold text-sm hover:opacity-90 transition-opacity whitespace-nowrap cursor-pointer"
            >
              Back to Login
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 px-4">
      <div className="w-full max-w-sm text-center">
        <div className="bg-white rounded-2xl border border-gray-100 p-10">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-violet-100 mx-auto mb-5">
            <i className="ri-loader-4-line text-2xl text-violet-600 animate-spin"></i>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Signing you in...</h2>
          <p className="text-sm text-gray-400">Just a moment while we set up your account.</p>
        </div>
      </div>
    </div>
  );
}
